import { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchHealthFacilitiesGeolocation, fetchUsersGeolocation, fetchUsersInRadius } from "../redux/UsersSlice";
import { toast } from "react-toastify";
import { Link, Loader2, MapPin, Search, UserCircle2, Monitor, Smartphone } from "lucide-react";
import axios from "axios";
import frontline_pin from '../assets/frontline-pin.png';
import intermediate_pin from '../assets/intermediate-pin.png';
import advanced_pin from '../assets/advanced-pin.png'
import focal from '../assets/pin.png'
import { IoClose } from "react-icons/io5";
import DisburseMessageModal from "../components/DisburseMessageModal";
import { fetchHealthFacilities } from "../redux/HealthFacilitiesSlice";
import { debounce } from "lodash";
import HealthFacility from "./HealthFacility";

// Custom icon
const createIcon = (form) => new L.Icon({
  iconUrl: form === 'user' ? intermediate_pin : form === "health-facility" ? frontline_pin : focal,
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [41, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Small screen warning component
const SmallScreenWarning = () => (
  <div className="fixed inset-0 bg-white flex flex-col items-center justify-center text-center p-6">
    <Smartphone size={64} className="text-gray-400 mb-4" />
    <h2 className="text-2xl font-bold mb-2">Device Not Supported</h2>
    <p className="text-gray-600 mb-6">
      This map interface requires a larger screen for optimal viewing.
      Please access this page using a tablet or desktop device.
    </p>
    <div className="flex items-center justify-center space-x-2 text-blue-600">
      <Monitor size={24} />
      <span className="font-medium">Recommended minimum width: 768px</span>
    </div>
  </div>
);

export default function UserMap() {
  const [focalPoint, setFocalPoint] = useState({ latitude: 5.6937, longitude: -0.1870 });
  const [radius, setRadius] = useState(50000);
  const dispatch = useDispatch();
  const users = useSelector(state => state.users.items);
  const [showUsers, setShowUsers] = useState(true);
  const [showFacilities, setShowFacilities] = useState(true);
  const [healthFacility, setHealthFacility] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768); // 768px is typical tablet breakpoint
    };
    
    // Initial check
    checkScreenSize();
    
    // Add resize listener
    window.addEventListener('resize', checkScreenSize);
    
    // Clean up
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Add state for selected users
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSettingLocation, setIsSettingLocation] = useState(false);
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [isGettingRadalUsers, setIsGettingRadalUsers] = useState(false);
  const [showCapturedUsersData, setShowCapturedUsersData] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)

  const [filters, setFilters] = useState({
    fetp_level: [],
    status: 'undefined',
    response_deployment: 'undefined'
  });

  const userTypes = ['Frontline', 'Intermediate', 'Advanced'];
  const statusOptions = [
    { value: 'undefined', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];
  const deploymentOptions = [
    { value: 'undefined', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' }
  ];

  useEffect(() => {
    handleFetchUsers();
  }, [filters, dispatch]);

  // Handle individual user selection
  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Handle select all users
  const handleSelectAllUsers = (checked) => {
    if (checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleFetchUsers = () => {
    const params = {};

    if (filters.fetp_level.length > 0) {
      params['filter[fetp_level]'] = filters.fetp_level;
    }
    if (filters.status !== 'undefined') {
      params['filter[status]'] = filters.status;
    }
    if (filters.response_deployment !== 'undefined') {
      params['filter[response_deployment]'] = filters.response_deployment;
    }

    dispatch(fetchUsersGeolocation(params))
      .unwrap()
      .then(response => {
        if (response.meta) {
          // Handle success if needed
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch users');
      });
  };

  const handleFetchHealthFacilites = () => {
    dispatch(fetchHealthFacilitiesGeolocation())
      .unwrap()
      .then(response => {
        if (response.meta) {
          // Handle success if needed
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to Health facilities users');
      });
  };

  const getUsersInRadius = async (latitude, longitude, radius) => {
    try {
      setIsGettingRadalUsers(true)
      const params = {};

      if (filters.fetp_level.length > 0) {
        params['filter[fetp_level]'] = filters.fetp_level;
      }
      if (filters.status !== 'undefined') {
        params['filter[status]'] = filters.status;
      }
      if (filters.response_deployment !== 'undefined') {
        params['filter[response_deployment]'] = filters.response_deployment;
      }

      const res = await dispatch(fetchUsersInRadius({ params, latitude, longitude, radius })).unwrap().then((resp) => {
        toast.success('Users Captured')
        setShowCapturedUsersData(true)
        // Reset selected users when new users are captured
        setSelectedUsers([]);
      })

    } catch (error) {
      toast.error(error.message || 'Failed to get users in radius')
    } finally {
      setIsGettingRadalUsers(false)
    }
  }

  const handleFETPCheckboxChange = (type) => {
    setShowCapturedUsersData(false)
    setFilters(prev => ({
      ...prev,
      fetp_level: prev.fetp_level.includes(type)
        ? prev.fetp_level.filter(t => t !== type)
        : [...prev.fetp_level, type]
    }));
  };

  const handleSelectAllFETP = (checked) => {
    setShowCapturedUsersData(false)
    setFilters(prev => ({
      ...prev,
      fetp_level: checked ? [...userTypes] : []
    }));
  };

  const handleStatusChange = (value) => {
    setShowCapturedUsersData(false)
    setFilters(prev => ({
      ...prev,
      status: value
    }));
  };

  const handleDeploymentChange = (value) => {
    setShowCapturedUsersData(false)
    setFilters(prev => ({
      ...prev,
      response_deployment: value
    }));
  };

  const handleUpdate = () => {
    setShowCapturedUsersData(false)
    const lat = parseFloat(selectedLocation.lat);
    const lon = parseFloat(selectedLocation.lng);

    if (!isNaN(lat) && !isNaN(lon)) {
      setFocalPoint({ latitude: lat, longitude: lon });
    }
    setTimeout(() => {
      setSelectedLocation(false)
    }, 1500)
  };

  const handleLocationSelect = async (locationInfo) => {
    setIsSelectingLocation(true);
    setShowCapturedUsersData(false)
    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${locationInfo.lat}&lon=${locationInfo.lng}&format=json`);
      const fullLocationInfo = {
        ...locationInfo,
        displayName: response.data.display_name
      };

      setSelectedLocation(fullLocationInfo);
      setSearchResults([]);
    } catch (error) {
      console.error('Error fetching location details:', error);
      setSelectedLocation(locationInfo);
      setSearchResults([]);
    } finally {
      setIsSelectingLocation(false);
    }
  };

  const handleSearch = async () => {
    setIsSearching(true);
    setShowCapturedUsersData(false)
    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/search?q=${searchQuery}&format=json`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching for location:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleMarkerDragEnd = (event) => {
    const marker = event.target;
    const newPosition = marker.getLatLng();

    // Update focal point state
    setFocalPoint({
      latitude: newPosition.lat,
      longitude: newPosition.lng
    });
  };

  const FacilitySearch = () => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const { loading, items: searchResults = [] } = useSelector((state) => state.healthFacilities);
    const [showResults, setShowResults] = useState(false);
    const resultsRef = useRef(null);

    // Handle clicks outside of the results dropdown to close it
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (resultsRef.current && !resultsRef.current.contains(event.target)) {
          setShowResults(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Debounce search to prevent excessive API calls
    const debouncedSearch = useCallback(
      debounce((term) => {
        if (term.trim()) {
          dispatch(fetchHealthFacilities({
            search: term,
          }));
          setShowResults(true);
        } else {
          setShowResults(false);
        }
      }, 500),
      [dispatch]
    );

    // Handle input changes
    const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchTerm(value);
      debouncedSearch(value);
    };

    // Clean up debounce on unmount
    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    return (
      <div className="relative w-full">
        <label className="text-sm">Health Facility:</label>
        {/* Search input */}
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => searchTerm.trim() && setShowResults(true)}
            placeholder="Search health facilities..."
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="w-5 h-5 border-t-2 border-blue-500 rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Results dropdown */}
        {showResults && searchResults.length > 0 && (
          <div
            ref={resultsRef}
            className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {searchResults.map((result, index) => (
              <div
                onClick={() => setHealthFacility(result)}
                key={result.id || index}
                className="p-2 hover:bg-gray-100 cursor-pointer flex flex-col justify-center border-b last:border-b-0"
              >
                <span className="font-medium">{result.name}</span>
                {result.region && (
                  <span className="text-sm text-gray-500">
                    {result.region} region
                  </span>
                )}
                <span className="text-sm text-gray-500">
                  <span className="font-bold">District: </span>{result?.district}
                </span>
                <span className="text-sm text-gray-500">
                  <span className="font-bold">Town: </span>{result?.town}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* No results message */}
        {showResults && searchTerm && searchResults.length === 0 && !loading && (
          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-center text-gray-500">
            No facilities found matching "{searchTerm}"
          </div>
        )}
      </div>
    );
  };

  // If mobile view, show warning instead of map interface
  if (isMobileView) {
    return <SmallScreenWarning />;
  }

  return (
    <div className="flex">
      <div className="w-1/4 p-4 max-h-screen overflow-auto">
        <h2 className="text-xl font-bold mb-4">Update Focal Point & Radius</h2>

        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for a location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2 border rounded"
            />
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isSearching ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <Search size={20} />
              )}
            </button>
          </div>
          {searchResults.length > 0 && (
            <ul className="mt-2 bg-white shadow-md rounded-md max-h-40 overflow-y-auto">
              {searchResults.map((result) => (
                <li
                  key={result.place_id}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm flex justify-between items-center"
                  onClick={() => handleLocationSelect({
                    lat: parseFloat(result.lat),
                    lng: parseFloat(result.lon),
                    displayName: result.display_name
                  })}
                >
                  {result.display_name}
                  <button className="text-blue-600 flex items-center">
                    {isSelectingLocation && (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selectedLocation && (
          <div className="p-4 bg-gray-100">
            <div className="mb-2">
              <p className="font-semibold flex items-center text-sm">
                <MapPin className="mr-2 text-blue-600" size={20} />
                {selectedLocation.displayName || 'Location Selected'}
              </p>
            </div>
            <div className="text-sm text-gray-700">
              <p>Latitude: {selectedLocation.lat}</p>
              <p>Longitude: {selectedLocation.lng}</p>
            </div>
            <button
              onClick={handleUpdate}
              disabled={isSettingLocation}
              className="mt-2 bg-blue-600 text-white px-3 py-1 rounded-lg shadow-lg text-sm hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              {isSettingLocation ? (
                <Loader2 className="animate-spin mr-2" size={20} />
              ) : (
                'Set Focal Point'
              )}
            </button>
          </div>
        )}

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700">Adjust Radius: {radius.toLocaleString()} meters</label>
          <input
            type="range"
            min="0"
            max="100000"
            step="500"
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
            className="w-full cursor-pointer mt-2"
          />
        </div>

        <FacilitySearch />

        <div className="mt-6 space-y-4">
          {/* Show Users Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Show Users</label>
            <div
              className="relative inline-flex items-center cursor-pointer w-11 h-6"
              onClick={() => setShowUsers((prev) => !prev)}
            >
              <div
                className={`w-11 h-6 rounded-full transition ${showUsers ? "bg-blue-600" : "bg-gray-200"
                  }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${showUsers ? "translate-x-5" : ""
                    }`}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            FETP Level
          </label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="select-all"
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
                checked={filters.fetp_level.length === userTypes.length}
                onChange={(e) => handleSelectAllFETP(e.target.checked)}
              />
              <label htmlFor="select-all" className="ml-2 text-sm text-gray-700">
                Select All
              </label>
            </div>
            {userTypes.map(type => (
              <div key={type} className="flex items-center">
                <input
                  type="checkbox"
                  id={type}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  checked={filters.fetp_level.includes(type)}
                  onChange={() => handleFETPCheckboxChange(type)}
                />
                <label htmlFor={type} className="ml-2 text-sm text-gray-700">
                  {type}
                </label>
              </div>
            ))}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Selected: {filters.fetp_level.length ? filters.fetp_level.join(', ') : 'None'}
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 my-2">
            Response Deployment
          </label>
          <select
            className="border border-gray-300 rounded-md p-2 text-xs w-full"
            value={filters.response_deployment}
            onChange={(e) => handleDeploymentChange(e.target.value)}
          >
            {deploymentOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 my-2">
            User Status
          </label>
          <select
            className="border border-gray-300 rounded-md p-2 text-xs w-full"
            value={filters.status}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {showCapturedUsersData && (
          <div className="mt-6">
            <div className="">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Captured Radal Users ({users?.length})
                </label>
                <IoClose size={20}
                  className="cursor-pointer"
                  onClick={() => {
                    setShowCapturedUsersData(false);
                    setSelectedUsers([]);
                  }}
                />
              </div>
              {/* Add select all checkbox */}
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="select-all-users"
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  checked={selectedUsers.length === users?.length}
                  onChange={(e) => handleSelectAllUsers(e.target.checked)}
                />
                <label htmlFor="select-all-users" className="ml-2 text-sm text-gray-700">
                  Select All Users
                </label>
              </div>
              <p className="text-blue-700 hover:underline cursor-pointer mb-2"
                onClick={() => {
                  if (selectedUsers.length === 0) {
                    toast.error('Please select at least one user');
                    return;
                  }
                  setShowMessageModal(true);
                }}
              >
                Disburse Communication ({selectedUsers.length} selected)
              </p>
            </div>
            <div className="gap-2 h-[30vh] overflow-auto">
              {users?.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow mb-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleUserSelect(user.id)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 mr-3"
                  />
                  <div className="flex-shrink-0">
                    {user.profile_img ? (
                      <img
                        src={user.profile_img}
                        alt="img"
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserCircle2 className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-medium text-gray-900">{user.name}</h3>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <MapContainer center={[focalPoint.latitude, focalPoint.longitude]} zoom={6} style={{ height: "100vh", width: "75%" }} className="relative z-0">
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        <Circle
          center={[focalPoint.latitude, focalPoint.longitude]}
          radius={radius}
          pathOptions={{
            fillColor: "blue",
            fillOpacity: 0.2,
            color: "red",
          }}
        />
        <Marker
          position={[focalPoint.latitude, focalPoint.longitude]}
          icon={createIcon('focal')}
          draggable={true}
          eventHandlers={{
            dragend: handleMarkerDragEnd
          }}
        >
          <Popup>
            <p>The Focal Point</p>
            <button
              onClick={() => { getUsersInRadius(focalPoint.latitude, focalPoint.longitude, radius) }}
              disabled={isGettingRadalUsers}
              className="bg-blue-600 text-white px-3 py-1 rounded-md shadow-lg text-sm hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              {isGettingRadalUsers ? (
                <Loader2 className="animate-spin mr-2" size={20} />
              ) : (
                'Capture Users In radius'
              )}
            </button>
          </Popup>
        </Marker>

        {showUsers && users?.filter(user => user?.lat && user?.long).map((user) => (
          <Marker
            key={user.id}
            position={[user.lat, user.long]}
            icon={createIcon('user')}
          >
            <Popup>
              <strong>{user?.name || 'Unknown User'}</strong> <br />
              {user?.location || 'Location not available'}
            </Popup>
          </Marker>
        ))}

        {healthFacility && <Marker
          key={healthFacility?.id}
          position={[healthFacility?.latitude, healthFacility?.longitude]}
          icon={createIcon('health-facility')}
        >
          <Popup>
            <strong>{healthFacility?.name}</strong> <br />
          </Popup>
        </Marker>}
      </MapContainer>

      {showMessageModal && (
        <DisburseMessageModal
          onClose={() => {
            setShowMessageModal(false);
          }}
          users={users.filter(user => selectedUsers.includes(user.id))}
        />
      )}
    </div>
  );
}